import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { RecipientsTable } from '../../components/communications/RecipientsTable';
import { partnerApi } from '../../lib/api';

export function CommunicationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const iframeContainerRef = useRef<HTMLDivElement>(null);
  const [iframeScale, setIframeScale] = useState(1);

  const type = (searchParams.get('type') as 'email' | 'sms') || 'email';
  const page = parseInt(searchParams.get('page') || '1');
  const limit = parseInt(searchParams.get('limit') || '50');

  // Get the filters from search params
  const filters: Record<string, string | number> = {
    type,
    page,
    limit,
  };
  if (searchParams.get('businesses')) filters.businesses = searchParams.get('businesses')!;
  if (searchParams.get('platforms')) filters.platforms = searchParams.get('platforms')!;
  if (searchParams.get('automationType')) filters.automationType = searchParams.get('automationType')!;
  if (searchParams.get('startDate')) filters.startDate = searchParams.get('startDate')!;
  if (searchParams.get('endDate')) filters.endDate = searchParams.get('endDate')!;
  if (searchParams.get('search')) filters.search = searchParams.get('search')!;

  // Fetch the list of communications (for navigation)
  const { data: communicationsData } = useQuery({
    queryKey: ['communications', type, page, filters],
    queryFn: () => partnerApi.getCommunications(filters),
  });

  const communications = useMemo(() => communicationsData?.communications || [], [communicationsData]);
  const currentIndex = communications.findIndex(comm => comm.communicationGroupId === id);
  const currentCommunication = communications[currentIndex];

  const navigateToCommunication = useCallback(
    (commId: string) => {
      const params = new URLSearchParams(searchParams);
      navigate(`/dashboard/communications/${commId}?${params.toString()}`);
    },
    [searchParams, navigate],
  );

  const handleBack = useCallback(() => {
    navigate('/dashboard/communications');
  }, [navigate]);

  const handlePrevious = useCallback(() => {
    if (currentIndex > 0 && communications[currentIndex - 1]) {
      const prevComm = communications[currentIndex - 1];
      navigateToCommunication(prevComm.communicationGroupId);
    }
  }, [currentIndex, communications, navigateToCommunication]);

  const handleNext = useCallback(() => {
    if (
      currentIndex >= 0 &&
      currentIndex < communications.length - 1 &&
      communications[currentIndex + 1]
    ) {
      const nextComm = communications[currentIndex + 1];
      navigateToCommunication(nextComm.communicationGroupId);
    }
  }, [currentIndex, communications, navigateToCommunication]);

  const canGoPrevious = currentIndex > 0;
  const canGoNext = currentIndex >= 0 && currentIndex < communications.length - 1;

  // Iframe scaling for email preview
  useEffect(() => {
    if (!iframeContainerRef.current || type !== 'email') return;

    const updateScale = () => {
      if (iframeContainerRef.current) {
        const containerWidth = iframeContainerRef.current.offsetWidth;
        // iframe is 800px wide, calculate scale to fit container width
        const newScale = containerWidth / 800;
        setIframeScale(newScale);
      }
    };

    updateScale();

    const resizeObserver = new ResizeObserver(updateScale);
    resizeObserver.observe(iframeContainerRef.current);

    return () => resizeObserver.disconnect();
  }, [type, currentCommunication]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNext();
      } else if (e.key === 'Escape') {
        handleBack();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handlePrevious, handleNext, handleBack]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!currentCommunication) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-medium text-gray-900 font-nav mb-2">Communication not found</h2>
          <button onClick={handleBack} className="text-blue-600 hover:text-blue-800 transition-colors">
            ← Back to communications
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:text-gray-900 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div>
            <h1 className="text-2xl font-medium text-gray-900 font-nav">
              {type === 'email' ? currentCommunication.emailSubject || 'Untitled Email' : 'SMS Message'}
            </h1>
            <p className="text-sm text-gray-600">
              {currentCommunication.businessName} •{' '}
              {formatDate(currentCommunication.communicationCreatedAt)}
            </p>
          </div>
        </div>

        {/* Navigation controls */}
        <div className="flex items-center gap-2">
          {currentIndex >= 0 && (
            <span className="text-sm text-gray-600 mr-2">
              {currentIndex + 1} of {communications.length}
            </span>
          )}
          <button
            onClick={handlePrevious}
            disabled={!canGoPrevious}
            className="p-1.5 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Previous (←)">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={handleNext}
            disabled={!canGoNext}
            className="p-1.5 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Next (→)">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-y-auto">
        {/* Main preview area */}
        <main className="p-6">
          <div className="max-w-7xl mx-auto">
            {/* Email Metadata */}
            {type === 'email' && (
              <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Email Details</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {currentCommunication.emailFromEmail && (
                    <div>
                      <div className="text-gray-500 font-medium">From Email</div>
                      <div className="text-gray-900 mt-1">{currentCommunication.emailFromEmail}</div>
                    </div>
                  )}
                  {currentCommunication.emailFromName && (
                    <div>
                      <div className="text-gray-500 font-medium">From Name</div>
                      <div className="text-gray-900 mt-1">{currentCommunication.emailFromName}</div>
                    </div>
                  )}
                  {currentCommunication.emailReplyTo && (
                    <div>
                      <div className="text-gray-500 font-medium">Reply-To</div>
                      <div className="text-gray-900 mt-1">{currentCommunication.emailReplyTo}</div>
                    </div>
                  )}
                  {currentCommunication.emailSubject && (
                    <div>
                      <div className="text-gray-500 font-medium">Subject</div>
                      <div className="text-gray-900 mt-1">{currentCommunication.emailSubject}</div>
                    </div>
                  )}
                  {currentCommunication.emailPreviewText && (
                    <div className="col-span-2">
                      <div className="text-gray-500 font-medium">Preview Text</div>
                      <div className="text-gray-900 mt-1">{currentCommunication.emailPreviewText}</div>
                    </div>
                  )}
                  {currentCommunication.automationName && (
                    <div>
                      <div className="text-gray-500 font-medium">Campaign</div>
                      <div className="text-gray-900 mt-1">{currentCommunication.automationName}</div>
                    </div>
                  )}
                  <div>
                    <div className="text-gray-500 font-medium">Sent At</div>
                    <div className="text-gray-900 mt-1">
                      {formatDate(currentCommunication.communicationCreatedAt)}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="text-2xl font-bold text-gray-900">
                  {currentCommunication.recipientCount.toLocaleString()}
                </div>
                <div className="text-xs text-gray-600 mt-1">Recipients</div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="text-2xl font-bold text-gray-900">
                  {currentCommunication.deliveredCount.toLocaleString()}
                </div>
                <div className="text-xs text-gray-600 mt-1">Delivered</div>
              </div>
              {type === 'email' && (
                <>
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="text-2xl font-bold text-gray-900">
                      {currentCommunication.engagementRate}%
                    </div>
                    <div className="text-xs text-gray-600 mt-1">Open Rate</div>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="text-2xl font-bold text-gray-900">
                      $
                      {parseFloat(currentCommunication.attributedRevenue || '0').toLocaleString(
                        undefined,
                        {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        },
                      )}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">Revenue</div>
                  </div>
                </>
              )}
            </div>

            {/* Content grid */}
            <div className="grid grid-cols-3 gap-6">
              {/* Left: Email/SMS preview - 2 columns */}
              <div className="col-span-2 bg-white rounded-lg border border-gray-200 overflow-hidden">
                {type === 'email' && currentCommunication.compiledHtml ? (
                  <div ref={iframeContainerRef} className="w-full h-[700px] overflow-auto">
                    <div
                      style={{
                        width: `${800 * iframeScale}px`,
                        height: `${1200 * iframeScale}px`,
                      }}>
                      <iframe
                        srcDoc={currentCommunication.compiledHtml}
                        title="Email preview"
                        sandbox="allow-same-origin"
                        className="border-none pointer-events-none origin-top-left"
                        style={{
                          width: '800px',
                          height: '1200px',
                          transform: `scale(${iframeScale})`,
                        }}
                      />
                    </div>
                  </div>
                ) : type === 'sms' ? (
                  <div className="p-6 h-[700px]">
                    <div className="bg-blue-500 text-white rounded-2xl rounded-bl-none p-4 max-w-md">
                      <div className="text-xs font-semibold mb-2">
                        {currentCommunication.companyName}
                      </div>
                      <div className="whitespace-pre-wrap">{currentCommunication.messageBody}</div>
                    </div>
                  </div>
                ) : (
                  <div className="p-6 text-center text-gray-500 h-[700px] flex items-center justify-center">
                    No content available
                  </div>
                )}
              </div>

              {/* Right: Recipients - 1 column */}
              <div className="col-span-1">
                <RecipientsTable communicationGroupId={currentCommunication.communicationGroupId} />
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
